import type { PlayInfo } from '@module/apis/client-side/live/type';
import type { useFetchLivePlayMutation } from '@module/queries/live/liveQueryHooks';
import type { GoPlayParams } from '@module/types/play/goPlayTypes';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { DEFAULT_EXCEL_SELECT_VALUE, useBroadcastSponPopoverStore } from '../../stores/useBroadcastSponPopoverStore';
import { useFetchLivePlayMutationStore } from '../../stores/useFetchLivePlayMutationStore';
import useGetLoginInfoQuery from '../auth/useGetLoginInfoQuery';

interface Props {
  goPlay: (
    media: GoPlayParams,
    mutateAsyncParams?: Partial<Parameters<ReturnType<typeof useFetchLivePlayMutation>['mutateAsync']>[0]>,
  ) => Promise<PlayInfo | undefined>;
  userId: string;
  enabled?: boolean;
}
/**
 * @description /play/[userId] 계열 라우트에 직접 진입했을 때 goPlay를 자동 실행해 playInfo를 보장하는 훅.
 * 썸네일 클릭처럼 상위 플로우에서 이미 goPlay가 호출된 자연스러운 진입 케이스는 store 값을 그대로 사용하고,
 * 새로고침 또는 URL 직접 입력처럼 goPlay 없이 진입한 케이스만 보완적으로 처리한다.
 */
const useAutoGoPlayOnDirectEntry = ({ goPlay, userId, enabled = true }: Props) => {
  const isShareLink = useSearchParams().get('sl') === '1';
  const { data: playInfo } = useFetchLivePlayMutationStore();
  const { isFetched: isLoginInfoFetched } = useGetLoginInfoQuery();
  const setDefaultExcelSelectValue = useBroadcastSponPopoverStore((state) => state.setDefaultExcelSelectValue);

  if (Array.isArray(userId)) throw new Error('userId must be a string');

  useEffect(() => {
    if (!enabled) return;
    if (!userId) return;
    if (!isLoginInfoFetched) return;
    const playInfoUserId = playInfo?.media.userId;
    if (playInfoUserId === userId) {
      // If the userId's playInfo is already called, we don't need to call goPlay again
      return;
    }

    const authenticateUserBeforePlay: Parameters<typeof goPlay>[0] = {
      userId,
      userNick: '',
      isLive: true,
      isAdult: false,
      isPw: false,
      type: 'free',
      user: 0,
      userLimit: Infinity,
    };

    const mutateAsyncParams: Parameters<typeof goPlay>[1] = {};
    if (isShareLink) mutateAsyncParams.shareLinkType = 'shareLink';

    goPlay(authenticateUserBeforePlay, mutateAsyncParams);
  }, [enabled, userId, isLoginInfoFetched]);

  useEffect(() => {
    return () => {
      if (userId && userId === useFetchLivePlayMutationStore.getState().data?.media.userId) {
        useFetchLivePlayMutationStore.getState().reset();
      }
      setDefaultExcelSelectValue(DEFAULT_EXCEL_SELECT_VALUE);
    };
  }, [userId]);

  return {
    playInfo,
  };
};

export default useAutoGoPlayOnDirectEntry;
